import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  IFilterAPI,
  SpaceItem,
} from 'src/app/standalones/components/generic-filters-view/filters.interface';
import { AclService } from 'src/app/services/acl.service';
import { institutionHasSites, LEARN_MORE_ABOUT_SITES } from 'src/app/utilities/sites.utils';
import { Observable, map } from 'rxjs';
import {
  DataSourceConfigOptions,
  DataSourceFetchParams,
  DataSourceFetchResult,
  PaginationStrategy,
} from 'src/app/services/data-source.service';
import { SelectionListModalSections } from 'src/app/standalones/components/modals/selection-modal/selection-list-modal/selection-list-modal.component';
import { Site, User } from '../../models/user';
import { SpacesService } from '../../services/spaces.service';
import { UserService } from '../../services/user.service';

export interface SelectSpaceDialogData {
  site?: Site;
  hideFooter?: boolean;
  showAllSpacesEvenIfNotHost?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-select-space-dialog',
  templateUrl: './select-space-dialog.component.html',
  styleUrls: ['./select-space-dialog.component.scss'],
})
export class SelectSpaceDialogComponent implements OnInit {
  LEARN_MORE_ABOUT_SITES = LEARN_MORE_ABOUT_SITES;

  @ViewChild('nameHeader', { static: true }) nameHeader!: TemplateRef<void>;
  @ViewChild('nameCell', { static: true }) nameCell!: TemplateRef<{ $implicit: SpaceItem }>;
  @ViewChild('ownerHeader', { static: true }) ownerHeader!: TemplateRef<void>;
  @ViewChild('ownerCell', { static: true }) ownerCell!: TemplateRef<{ $implicit: SpaceItem }>;

  columns!: SelectionListModalSections<SpaceItem>['columns'];
  fetchConfig: DataSourceConfigOptions<SpaceItem>;

  user?: User;
  canCreateSpace = false;
  site?: Site;
  hideFooter?: boolean;
  isSitesEnabled = false;

  constructor(
    private dialogRef: MatDialogRef<SelectSpaceDialogComponent>,
    private spacesService: SpacesService,
    private userService: UserService,
    private aclService: AclService,
    @Inject(MAT_DIALOG_DATA)
    private data: SelectSpaceDialogData = {},
  ) {
    this.site = this.data?.site;
    this.hideFooter = this.data?.hideFooter;

    this.fetch = this.fetch.bind(this);
    this.fetchConfig = {
      fetch: this.fetch,
      paginationStrategy: PaginationStrategy.ScrollBased,
    };
  }

  ngOnInit(): void {
    this.columns = {
      name: { size: 0.5, header: this.nameHeader, cell: this.nameCell },
      owner: { header: this.ownerHeader, cell: this.ownerCell },
    };

    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      if (this.user) {
        this.canCreateSpace = this.aclService.checkUserCanCreateSpace(this.user);
        this.isSitesEnabled = institutionHasSites(this.user.institution);
      }
    });
  }

  fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<SpaceItem>> {
    const filters: IFilterAPI = {
      ...params.filter,
      ...(this.site ? { sites: [this.site._id] } : {}),
      ...(this.user?.institution ? { institution: this.user.institution._id } : {}),
      ...(this.user && !this.userShouldSeeAllSpacesEvenIfNotHost()
        ? { hosts: [this.user._id] }
        : {}),
    };

    return this.spacesService
      .getSpacesList(filters, params.pageIndex + 1, params.pageSize, undefined, true)
      .pipe(
        map((res) => ({
          items: res.sessions.map((session) => ({
            ...session,
            // Since session.owner is the owner's ID, we need to find the respective User object
            // in the populatedUsers array to display the owner's name and profile photo using app-profile-photo.
            // This also assumes that the owner is always found in the populatedUsers array.
            owner: session.populatedUsers.find((user) => user?._id === session.owner)!,
          })),
          totalItems: res.totalRecords,
        })),
      );
  }

  private userShouldSeeAllSpacesEvenIfNotHost(): boolean {
    return (
      !!this.user &&
      (!!this.user.institution?.settings?.addAdminsAsHostsInSpace ||
        !!this.data?.showAllSpacesEvenIfNotHost) &&
      (this.aclService.isAdmin(this.user) || this.aclService.isSiteAdmin(this.user))
    );
  }

  showCreateSpaceDialog(): void {
    this.dialogRef.close('showCreateSpaceDialog');
  }
}
